import React from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Link,
  Card,
  CardContent,
} from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'

const OktaIntegrationGuide = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Okta OIN SCIM Integration Guide
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Configure SCIM provisioning between Okta and Realty.com to
            automatically manage user accounts
          </Typography>
          <Divider />
        </Box>

        <Alert severity="warning" sx={{ mb: 4, border: '2px solid #ff9800' }}>
          <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
            ⚠️ Before You Begin
          </AlertTitle>
          <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
            Contact{' '}
            <Link
              href="mailto:support@realty.com"
              underline="hover"
              sx={{ fontWeight: 'bold' }}
            >
              support@realty.com
            </Link>{' '}
            to request your unique <strong>Organization ID (org_id)</strong> and{' '}
            <strong>API Token</strong> for SCIM integration. You will need both
            of these credentials to complete the setup process.
          </Typography>
        </Alert>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 2 }}>
            Overview
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6, mb: 3 }}
          >
            The Realty.com SCIM integration allows Okta to automatically
            provision and deprovision users in your Realty.com organization.
            When users are assigned to the Realty.com app in Okta, they will
            automatically be onboarded and subscribed to the appropriate plan.
          </Typography>

          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{ mb: 2, fontWeight: 'bold' }}
          >
            SCIM Supported Features
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Create Users</strong> - Automatically create user accounts
            when assigned in Okta
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Update User Attributes</strong> - Sync user profile
            changes from Okta to Realty.com
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Deactivate Users</strong> - Automatically deactivate
            accounts when unassigned in Okta
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Import Users</strong> - Import users from Realty.com to
            Okta
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Import Group feature is not supported
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
            Okta Configuration Steps
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Step 1: Configure Initial Settings
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Start by setting up your organization in the Realty.com
                  application:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Navigate to your Realty.com application in Okta" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Paste your Organization ID (org_id) in the org_id textbox" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="3. This org_id should be provided by support@realty.com"
                      secondary="Reference: initial_step.png shows the org_id input field"
                    />
                  </ListItem>
                </List>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    📷 Visual reference for the org_id textbox:
                  </Typography>
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src="/static/initial_step.png"
                    alt="Initial step showing org_id textbox"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Step 2: Enable API Integration and Configure Token
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Configure SCIM provisioning by enabling the API integration:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Navigate to the 'Provisioning' tab" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Click 'Configure API Integration'" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Check 'Enable API integration'" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="4. Paste your API Token in the API Token textbox" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="5. Click 'Test API Credentials' to verify the connection"
                      secondary="This step is required to ensure your API token and configuration are correct"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="6. Click 'Save' to confirm the API integration settings"
                      secondary="You should see a success message indicating the connection was established"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    API Token:
                  </Typography>
                  <Paper
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      fontFamily: 'monospace',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      YOUR_API_TOKEN_FROM_SUPPORT
                    </Typography>
                  </Paper>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    📷 Visual references for API integration setup:
                  </Typography>
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    1. Click &ldquo;Configure API Integration&rdquo;:
                  </Typography>
                  <img
                    src="/static/enable_provisioning.png"
                    alt="Enable provisioning and configure API integration button"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '16px',
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    2. Paste API Token in the textbox:
                  </Typography>
                  <img
                    src="/static/api_token.png"
                    alt="API Token input field"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Step 3: Enable Provisioning Features
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  After successfully testing the API connection, enable the
                  following provisioning options:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Navigate to the 'Provisioning' → 'To App' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Click 'Edit' to modify provisioning settings" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Enable the following options:" />
                  </ListItem>
                </List>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    ml: 3,
                    mb: 2,
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox checked disabled size="small" />}
                    label="Create Users"
                  />
                  <FormControlLabel
                    control={<Checkbox checked disabled size="small" />}
                    label="Update User Attributes"
                  />
                  <FormControlLabel
                    control={<Checkbox checked disabled size="small" />}
                    label="Deactivate Users"
                  />
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="4. Click 'Save' to apply the provisioning settings"
                      secondary="These settings determine what actions Okta can perform on user accounts"
                    />
                  </ListItem>
                </List>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    📷 Visual reference for provisioning options:
                  </Typography>
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src="/static/provision_option.png"
                    alt="Provisioning options showing Create Users and Deactivate Users enabled"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Step 4: Configure Profile Mapping
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Configure how user attributes are mapped between Okta and
                  Realty.com:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Navigate to the 'Provisioning' → 'To App' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Scroll down to 'Attribute Mappings'" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Review the default mappings:" />
                  </ListItem>
                </List>
                <Box sx={{ ml: 3, mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    Standard Mappings:
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="userName → Email"
                        secondary="Primary identifier for user accounts"
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="firstName → First Name"
                        secondary="User's given name"
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="lastName → Last Name"
                        secondary="User's family name"
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="active → Active Status"
                        secondary="Determines if user account is active"
                      />
                    </ListItem>
                  </List>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    📷 Visual reference for profile mapping configuration:
                  </Typography>
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src="/static/profile_mapping.png"
                    alt="Profile mapping configuration showing required attribute mappings"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </Box>

                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="4. Modify mappings if needed for your organization"
                      secondary="Most organizations can use the default mappings"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="5. Click 'Save Mappings' if any changes were made" />
                  </ListItem>
                </List>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Contact support@realty.com if you
                    need help with attribute mapping.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }} id="sso">
              <CardContent>
                <Typography
                  variant="h4"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 'bold', color: 'primary.main' }}
                >
                  🔐 OIDC/SSO Configuration Steps
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  To set up Okta SSO, follow the instructions given below.
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Alternatively, you may book a call with us, and we&apos;ll be
                  happy to walk through the setup with you. Be sure to invite
                  your Okta Admin.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Requirements
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    • Admin access to an Okta org
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Supported Features
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    •{' '}
                    <strong>
                      Service Provider (SP)-Initiated Authentication (SSO) Flow
                    </strong>{' '}
                    - this authentication flow occurs when the user attempts to
                    log in to the application from Realty.com.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    • User Federation, role management, etc. are not supported.
                    Users and their Realty.com roles will still need to be
                    managed from the Realty.com settings page.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Configuration Steps
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="1. In Okta, Go to Applications → Browse App Catalog, and search and add Realty.com"
                        secondary="Link to Realty.com's App on Okta Integration Network"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="2. Assign the users or groups that should be able to log into Realty.com" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="3. Go to the Realty.com App → Sign On tab and note the Client ID and Client Secret" />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="4. Contact support@realty.com with the following information:"
                        secondary="• Okta Domain (looks like acme.okta.com) • Client ID • Client Secret"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="5. You will be provided with your Realty.com SSO URL" />
                    </ListItem>
                  </List>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Logging Into Realty.com Using Okta
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Once the Okta SSO is setup, users can login using the
                    provided SSO URL:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="1. Use the Realty.com SSO URL provided by support" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="2. Enter the email address" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="3. Login with Okta" />
                    </ListItem>
                  </List>
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Step 5: Configure Application Username Format
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Set the username format to ensure proper user identification:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Navigate to the 'Sign On' tab" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Click 'Edit' for the application username format" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Set the application username format to 'Email'" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="4. Save the configuration" />
                  </ListItem>
                </List>

                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    Required Setting:
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Username format:
                    </Typography>
                    <Paper
                      sx={{
                        p: 1,
                        bgcolor: 'grey.100',
                        fontFamily: 'monospace',
                      }}
                    >
                      <Typography variant="body2">Email</Typography>
                    </Paper>
                  </Box>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    📷 Visual reference for username format configuration:
                  </Typography>
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src="/static/application_username_format.png"
                    alt="Application username format configuration interface"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Paper>
    </Container>
  )
}

export default OktaIntegrationGuide
